import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import CategoryBrowseModal from '../../../../components/CategoryBrowseModal/CategoryBrowseModal';
import { useCategories } from '../../../../hooks/useCategories';
import { useToast } from '../../../../contexts/ToastContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { interpolate } from '../../../../components/BusinessHoursMatrix/interpolate';
import { queryKeys } from '../../../../lib/queryKeys';
import { resolveImageUrl } from '../../../../utils/imageUrl';
import type { Category } from '../../../../types/api-responses';
import styles from './Section.module.css';
import s from './CategoriesSection.module.css';

/**
 * CR-2026-05-15 — Categories sub-section (My Business shell).
 *
 * Drives the marketplace taxonomy that does five jobs in Devanza:
 *   1. Search & filter on the consumer marketplace ("salons that do Henna")
 *   2. Badge row on the salon's public profile
 *   3. Service catalog grouping inside the Business Portal
 *   4. SEO landing pages owned by marketing
 *   5. Future: paid add-on for "Featured in this category"
 *
 * Owners come here AFTER setting up Branches and BEFORE adding Services — a
 * service must belong to a category that the business is subscribed to.
 *
 * The educational hero at the top explains all of the above so an owner
 * understands why this step matters before they click Browse. After the first
 * successful subscribe, the hero collapses into a thin "Why again?" link
 * (state persisted via localStorage so the hero stays out of the way on
 * subsequent visits).
 *
 * BE contract:
 *   - GET /category/business            → subscribed list
 *   - GET /category                     → master list (all active)
 *   - POST /category/business/{id}      → subscribe (idempotent)
 *   - DELETE /category/business/{id}    → unsubscribe
 *
 * AB#1872 — the owner "request a custom category" flow was removed; owners are
 * subscribe-only against the SuperAdmin-managed master taxonomy.
 */

const HERO_DISMISS_KEY = 'devanza:my-business:categories-hero-dismissed';

function isHeroDismissed(): boolean {
    try {
        return window.localStorage.getItem(HERO_DISMISS_KEY) === '1';
    } catch {
        return false;
    }
}

function setHeroDismissed(value: boolean) {
    try {
        if (value) {
            window.localStorage.setItem(HERO_DISMISS_KEY, '1');
        } else {
            window.localStorage.removeItem(HERO_DISMISS_KEY);
        }
    } catch {
        // ignore quota / disabled storage
    }
}

const CategoriesSection: React.FC = () => {
    const { showToast } = useToast();
    const { t, language } = useLanguage();
    // O3 — show the Arabic category name in Arabic (falls back to the EN name).
    const catName = (c: Category) => (language === 'ar' && c.nameAr ? c.nameAr : c.name);
    const queryClient = useQueryClient();
    const {
        getBusinessCategories,
        getCategories,
        subscribeBusinessCategory,
        removeCategoryFromBusiness,
        loading,
    } = useCategories();

    const [subscribed, setSubscribed] = useState<Category[] | null>(null);
    const [master, setMaster] = useState<Category[] | null>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const [heroDismissed, setHeroDismissedState] = useState<boolean>(isHeroDismissed);
    const [browseOpen, setBrowseOpen] = useState(false);

    const invalidateCategoryCaches = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.categories.business() });
    }, [queryClient]);

    const loadSubscribed = useCallback(async () => {
        setFetchError(null);
        try {
            const bus = await getBusinessCategories();
            setSubscribed((bus?.categories ?? []).filter(c => c.isActive));
        } catch (err) {
            const msg = t('mb.categories.loadFailed');
            setFetchError(msg);
            if (subscribed === null) showToast(msg, 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getBusinessCategories, showToast, t]);

    const loadMaster = useCallback(async () => {
        if (master !== null) return;
        try {
            const res = await getCategories();
            setMaster((res?.categories ?? []).filter(c => c.isActive));
        } catch (err) {
            showToast(t('mb.categories.masterLoadFailed'), 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getCategories, showToast, t]);

    useEffect(() => {
        loadSubscribed();
    }, [loadSubscribed]);

    /* ── Hero handlers ─────────────────────────────────────────────── */
    const dismissHero = () => {
        setHeroDismissed(true);
        setHeroDismissedState(true);
    };
    const reopenHero = () => {
        setHeroDismissed(false);
        setHeroDismissedState(false);
    };

    /* ── Browse modal handlers ─────────────────────────────────────── */
    const openBrowse = async () => {
        await loadMaster();
        setBrowseOpen(true);
    };

    const handleSubscribe = async (categoryId: number) => {
        try {
            await subscribeBusinessCategory(categoryId);
            invalidateCategoryCaches();
            await loadSubscribed();
            // First-subscribe collapses the hero so subsequent visits go
            // straight to the working UX. Owner can reopen via "Why again?".
            if (!heroDismissed && (subscribed?.length ?? 0) === 0) {
                dismissHero();
            }
        } catch {
            showToast(t('mb.categories.addFailed'), 'error');
        }
    };

    const handleRemove = async (row: Category) => {
        const ok = window.confirm(
            interpolate(t('mb.categories.removeConfirm'), { name: catName(row) }),
        );
        if (!ok) return;
        try {
            await removeCategoryFromBusiness(row.id);
            showToast(interpolate(t('mb.categories.removed'), { name: catName(row) }), 'success');
            invalidateCategoryCaches();
            await loadSubscribed();
        } catch {
            showToast(t('mb.categories.removeFailed'), 'error');
        }
    };

    /* ── Derived view state ────────────────────────────────────────── */
    const isInitialLoading = loading && subscribed === null;
    const subscribedIds = useMemo(
        () => new Set((subscribed ?? []).map(c => c.id)),
        [subscribed],
    );
    const showEmpty = !isInitialLoading && !fetchError && (subscribed?.length ?? 0) === 0;
    const showList = !isInitialLoading && !fetchError && (subscribed?.length ?? 0) > 0;
    const showHero = !heroDismissed;

    return (
        <div>
            <header className={styles.header}>
                <h1>{t('mb.categories.title')}</h1>
                <p>{t('mb.categories.subtitle')}</p>
            </header>

            {/* Educational hero — collapsible, persisted via localStorage */}
            {showHero && (
                <div className={s.heroCard}>
                    <button
                        type="button"
                        className={s.heroCollapse}
                        onClick={dismissHero}
                        aria-label={t('mb.categories.hero.hide')}
                        title={t('mb.categories.hero.hide')}
                    >
                        ×
                    </button>
                    <div className={s.heroHeadlineRow}>
                        <div className={s.heroIcon} aria-hidden="true">🏷️</div>
                        <h2 className={s.heroHeadline}>{t('mb.categories.hero.title')}</h2>
                    </div>
                    <p className={s.heroBody}>{t('mb.categories.hero.body')}</p>
                    <ul className={s.heroBenefits}>
                        <li className={s.heroBenefit}>
                            <span className={s.heroBenefitIcon} aria-hidden="true">🔍</span>
                            <div className={s.heroBenefitText}>
                                <strong>{t('mb.categories.hero.searchTitle')}</strong>
                                <span>{t('mb.categories.hero.searchBody')}</span>
                            </div>
                        </li>
                        <li className={s.heroBenefit}>
                            <span className={s.heroBenefitIcon} aria-hidden="true">🪪</span>
                            <div className={s.heroBenefitText}>
                                <strong>{t('mb.categories.hero.badgesTitle')}</strong>
                                <span>{t('mb.categories.hero.badgesBody')}</span>
                            </div>
                        </li>
                        <li className={s.heroBenefit}>
                            <span className={s.heroBenefitIcon} aria-hidden="true">📈</span>
                            <div className={s.heroBenefitText}>
                                <strong>{t('mb.categories.hero.marketingTitle')}</strong>
                                <span>{t('mb.categories.hero.marketingBody')}</span>
                            </div>
                        </li>
                    </ul>
                    <div className={s.heroPreview}>
                        <span className={s.heroPreviewLabel}>{t('mb.categories.hero.previewLabel')}</span>
                        {(subscribed?.length ?? 0) > 0 ? (
                            subscribed!.slice(0, 5).map(c => (
                                <span key={c.id} className={s.heroPreviewChip}>{catName(c)}</span>
                            ))
                        ) : (
                            <span className={s.heroPreviewEmpty}>
                                {t('mb.categories.hero.previewEmpty')}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* "Why again?" link when hero is collapsed */}
            {!showHero && (
                <button type="button" className={s.heroReopenLink} onClick={reopenHero}>
                    {t('mb.categories.hero.reopen')}
                </button>
            )}

            {/* Loading skeleton */}
            {isInitialLoading && (
                <div aria-busy="true">
                    {[0, 1, 2].map(i => (
                        <div key={i} className={s.skeletonCard} style={{ marginBottom: 10 }}>
                            <div className={`${s.skeletonLine} ${s.skeletonLineMed}`} />
                            <div className={`${s.skeletonLine} ${s.skeletonLineShort}`} />
                        </div>
                    ))}
                </div>
            )}

            {/* Error banner with retry */}
            {fetchError && subscribed === null && (
                <div className={styles.intro} role="alert">
                    <span className={styles.introIcon}>⚠️</span>
                    <div>
                        <strong>{fetchError}</strong>{' '}
                        <button
                            type="button"
                            onClick={loadSubscribed}
                            className={styles.btn}
                            style={{ marginInlineStart: 12 }}
                        >
                            {t('mb.common.tryAgain')}
                        </button>
                    </div>
                </div>
            )}

            {/* Empty state — owner has zero subscribed categories */}
            {showEmpty && (
                <div className={s.empty}>
                    <div className={s.emptyIcon} aria-hidden="true">🏷️</div>
                    <h2 className={s.emptyTitle}>{t('mb.categories.empty.title')}</h2>
                    <p className={s.emptyDesc}>{t('mb.categories.empty.desc')}</p>
                    <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={openBrowse}
                    >
                        {t('mb.categories.browse')}
                    </button>
                </div>
            )}

            {/* Populated list */}
            {showList && (
                <>
                    <div className={s.toolbar}>
                        <div>
                            <span className={s.count}>
                                {interpolate(
                                    t(subscribed!.length === 1 ? 'mb.categories.count.one' : 'mb.categories.count.other'),
                                    { n: subscribed!.length },
                                )}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={openBrowse}
                        >
                            {t('mb.categories.browse')}
                        </button>
                    </div>

                    <ul className={s.cardList}>
                        {subscribed!.map(row => (
                            <li key={row.id} className={s.card}>
                                <div className={s.cardThumb}>
                                    {/* O3 (SO-38) — resolve the asset URL and fall back to
                                        the tag glyph if the image fails, never a broken icon. */}
                                    <CategoryThumb url={row.imageUrl} />
                                </div>
                                <div className={s.cardMain}>
                                    <div className={s.cardName}>{catName(row)}</div>
                                    <div className={s.cardMeta}>{t('mb.categories.cardMeta')}</div>
                                </div>
                                <button
                                    type="button"
                                    className={s.cardRemove}
                                    onClick={() => handleRemove(row)}
                                    aria-label={interpolate(t('mb.categories.removeAria'), { name: catName(row) })}
                                    title={t('mb.common.remove')}
                                >
                                    ×
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {browseOpen && (
                <CategoryBrowseModal
                    isOpen={browseOpen}
                    onClose={() => setBrowseOpen(false)}
                    master={master ?? []}
                    subscribedIds={subscribedIds}
                    onSubscribe={handleSubscribe}
                    loading={loading}
                />
            )}
        </div>
    );
};

/** O3 — category image with a glyph fallback when missing or broken. */
const CategoryThumb: React.FC<{ url?: string | null }> = ({ url }) => {
    const [failed, setFailed] = useState(false);
    useEffect(() => { setFailed(false); }, [url]);
    if (!url || failed) return <span aria-hidden="true">🏷️</span>;
    return <img src={resolveImageUrl(url)} alt="" onError={() => setFailed(true)} />;
};

export default CategoriesSection;
